import type {
	PartCodingCandidate,
	PartCodingRow,
	PartCodingStatus
} from '../../models/part-coding.model';

export const PART_CODING_STATUSES: readonly PartCodingStatus[] = [
	'Auto',
	'Manual',
	'Review',
	'Out of Stock',
	'RFQ'
];

export const DEFAULT_PASTE_TEXT = `1    8" to 4" stainless reducer
1    20' 8" sch 40 stainless pipe
2    8" 150# SS Weld Neck Flange
3    8" schedule 40 stainless weld neck 45 degree elbow
8" schedule 40 stianless weld neck tee
1    10" - Lug, DI Body, SS Stem, SS Disc, Resilient Seat, Gear
1    8" - Lug, SS Body, SS Stem, SS Disc, High Performance, Gear
1    8" - SS Body and Disc, Viton Seat, Split Disc Swing
1    Ball, 200 psi, FNPT, FP, SS Body, LO Spring Return Handle, NACE
1    Bleed Ring w/ 2 x 1/2 in NPT taps
2    10" 150# SS Weld Neck Flange
1    10" 150# SS Wide Flange
1    10" CGI
1    10" 150 CGI Gasket
1    6" 150# SS Blind Flange`;

// Candidate part codes an ISR can swap a line to, keyed by item family so the
// same list backs every row the matcher groups under it.
export const PART_CODING_CANDIDATES: Readonly<
	Record<string, readonly PartCodingCandidate[]>
> = {
	fl10: [
		{
			code: '36754',
			description: '10 150 SCH/40S STD RFWN FLANGE 316SS IMPORT',
			matchPercent: 100,
			inventory: 714,
			isMarketBasket: true
		},
		{
			code: '36754A',
			description: '10 150 SCH/40S STD RFWN FLANGE 316SS APPROVED IMPORT',
			matchPercent: 97,
			inventory: 604,
			isMarketBasket: true
		},
		{
			code: '36716',
			description: '10 150 SCH/10S RFWN FLANGE 316SS IMPORT',
			matchPercent: 95,
			inventory: 45,
			isMarketBasket: false
		},
		{
			code: '0-51570-6',
			description: '10 150 SCH/20 RFWN 316SS',
			matchPercent: 91,
			inventory: 74,
			isMarketBasket: false
		},
		{
			code: '36754D',
			description: '10 150 SCH/40S STD RFWN FLANGE 316SS DOMESTIC',
			matchPercent: 89,
			inventory: 8,
			isMarketBasket: true
		}
	],
	fl10w: [
		{
			code: '36754B',
			description: '10 150 SCH/40S STD WIDE FLANGE 316SS IMPORT',
			matchPercent: 85,
			inventory: 500,
			isMarketBasket: true
		},
		{
			code: '36754',
			description: '10 150 SCH/40S STD RFWN FLANGE 316SS IMPORT',
			matchPercent: 81,
			inventory: 714,
			isMarketBasket: false
		},
		{
			code: '36758W',
			description: '10 300 SCH/40S WIDE RFWN FLANGE 316SS IMPORT',
			matchPercent: 74,
			inventory: 32,
			isMarketBasket: false
		},
		{
			code: '36716',
			description: '10 150 SCH/10S RFWN FLANGE 316SS IMPORT',
			matchPercent: 68,
			inventory: 45,
			isMarketBasket: false
		},
		{
			code: '0-51570-6',
			description: '10 150 SCH/20 RFWN 316SS',
			matchPercent: 61,
			inventory: 74,
			isMarketBasket: false
		}
	],
	fl8: [
		{
			code: '36752',
			description: '8 150 SCH/40S STD RFWN FLANGE 316SS IMPORT',
			matchPercent: 98,
			inventory: 318,
			isMarketBasket: true
		},
		{
			code: '36752A',
			description: '8 150 SCH/40S STD RFWN FLANGE 316SS APPROVED IMPORT',
			matchPercent: 94,
			inventory: 120,
			isMarketBasket: true
		},
		{
			code: '36714',
			description: '8 150 SCH/10S RFWN FLANGE 316SS IMPORT',
			matchPercent: 87,
			inventory: 66,
			isMarketBasket: false
		},
		{
			code: '36752D',
			description: '8 150 SCH/40S STD RFWN FLANGE 316SS DOMESTIC',
			matchPercent: 84,
			inventory: 14,
			isMarketBasket: true
		}
	],
	pipe8: [
		{
			code: '30820',
			description: '8 SCH/40S SMLS PIPE 316SS A312 20FT RL',
			matchPercent: 96,
			inventory: 1240,
			isMarketBasket: true
		},
		{
			code: '30820W',
			description: '8 SCH/40S WELDED PIPE 316SS A312 20FT RL',
			matchPercent: 90,
			inventory: 880,
			isMarketBasket: true
		},
		{
			code: '30818',
			description: '8 SCH/10S SMLS PIPE 316SS A312 20FT RL',
			matchPercent: 82,
			inventory: 410,
			isMarketBasket: false
		},
		{
			code: '30822',
			description: '8 SCH/80S SMLS PIPE 316SS A312 20FT RL',
			matchPercent: 78,
			inventory: 95,
			isMarketBasket: false
		}
	],
	el45: [
		{
			code: '37145',
			description: '8 SCH/40S 45 DEG LR ELBOW 316SS BW IMPORT',
			matchPercent: 93,
			inventory: 62,
			isMarketBasket: true
		},
		{
			code: '37145D',
			description: '8 SCH/40S 45 DEG LR ELBOW 316SS BW DOMESTIC',
			matchPercent: 88,
			inventory: 9,
			isMarketBasket: true
		},
		{
			code: '37143',
			description: '8 SCH/10S 45 DEG LR ELBOW 316SS BW IMPORT',
			matchPercent: 80,
			inventory: 37,
			isMarketBasket: false
		},
		{
			code: '37155',
			description: '8 SCH/40S 90 DEG LR ELBOW 316SS BW IMPORT',
			matchPercent: 64,
			inventory: 148,
			isMarketBasket: true
		}
	],
	tee8: [
		{
			code: '37208',
			description: '8 SCH/40S STRAIGHT TEE 316SS BW IMPORT',
			matchPercent: 91,
			inventory: 41,
			isMarketBasket: true
		},
		{
			code: '37208D',
			description: '8 SCH/40S STRAIGHT TEE 316SS BW DOMESTIC',
			matchPercent: 84,
			inventory: 6,
			isMarketBasket: true
		},
		{
			code: '37206',
			description: '8 SCH/10S STRAIGHT TEE 316SS BW IMPORT',
			matchPercent: 77,
			inventory: 22,
			isMarketBasket: false
		},
		{
			code: '37210',
			description: '8 X 6 SCH/40S REDUCING TEE 316SS BW IMPORT',
			matchPercent: 58,
			inventory: 11,
			isMarketBasket: false
		}
	],
	red8x4: [
		{
			code: '37412',
			description: '8 X 4 SCH/40S CONC REDUCER 316SS BW IMPORT',
			matchPercent: 92,
			inventory: 26,
			isMarketBasket: true
		},
		{
			code: '37416',
			description: '8 X 4 SCH/40S ECC REDUCER 316SS BW IMPORT',
			matchPercent: 81,
			inventory: 18,
			isMarketBasket: false
		},
		{
			code: '37414',
			description: '8 X 4 SCH/10S CONC REDUCER 316SS BW IMPORT',
			matchPercent: 73,
			inventory: 31,
			isMarketBasket: false
		},
		{
			code: '37418',
			description: '8 X 6 SCH/40S CONC REDUCER 316SS BW IMPORT',
			matchPercent: 49,
			inventory: 44,
			isMarketBasket: false
		}
	],
	bfly10: [
		{
			code: '41330',
			description:
				'10 LUG BFLY DI BODY SS STEM SS DISC EPDM SEAT GEAR OP',
			matchPercent: 93,
			inventory: 12,
			isMarketBasket: true
		},
		{
			code: '41330V',
			description:
				'10 LUG BFLY DI BODY SS STEM SS DISC BUNA SEAT GEAR OP',
			matchPercent: 86,
			inventory: 5,
			isMarketBasket: false
		},
		{
			code: '41332',
			description:
				'10 LUG BFLY DI BODY SS STEM SS DISC EPDM SEAT LEVER OP',
			matchPercent: 79,
			inventory: 21,
			isMarketBasket: false
		},
		{
			code: '41340',
			description: '10 WAFER BFLY DI BODY SS DISC EPDM SEAT GEAR OP',
			matchPercent: 68,
			inventory: 8,
			isMarketBasket: false
		}
	],
	bfly8hp: [
		{
			code: '41528',
			description: '8 LUG HP BFLY SS BODY SS DISC RPTFE SEAT GEAR OP',
			matchPercent: 41,
			inventory: 0,
			isMarketBasket: false
		},
		{
			code: '41530',
			description: '8 LUG HP BFLY CS BODY SS DISC RPTFE SEAT GEAR OP',
			matchPercent: 36,
			inventory: 0,
			isMarketBasket: false
		},
		{
			code: '41338',
			description: '8 LUG BFLY DI BODY SS DISC EPDM SEAT GEAR OP',
			matchPercent: 29,
			inventory: 17,
			isMarketBasket: false
		}
	],
	chk8: [
		{
			code: '43120',
			description: '8 150 SPLIT DISC WAFER CHECK SS BODY VITON SEAT',
			matchPercent: 93,
			inventory: 18,
			isMarketBasket: true
		},
		{
			code: '43118',
			description: '8 150 SWING CHECK CS BODY SS DISC',
			matchPercent: 71,
			inventory: 4,
			isMarketBasket: false
		},
		{
			code: '43122',
			description: '8 150 DUO CHECK SS BODY EPDM SEAT',
			matchPercent: 64,
			inventory: 2,
			isMarketBasket: false
		}
	],
	ball2: [
		{
			code: '44870',
			description:
				'2 3000 FNPT FP BALL VALVE 316SS LO SPRING RETURN NACE',
			matchPercent: 91,
			inventory: 88,
			isMarketBasket: true
		},
		{
			code: '44868',
			description: '2 2000 FNPT FP BALL VALVE 316SS LEVER OP NACE',
			matchPercent: 75,
			inventory: 14,
			isMarketBasket: false
		},
		{
			code: '44872',
			description:
				'2 3000 FNPT RP BALL VALVE 316SS LO SPRING RETURN NACE',
			matchPercent: 68,
			inventory: 7,
			isMarketBasket: false
		},
		{
			code: '44866',
			description:
				'1-1/2 3000 FNPT FP BALL VALVE 316SS LO SPRING RETURN NACE',
			matchPercent: 51,
			inventory: 11,
			isMarketBasket: false
		}
	],
	bleed10: [
		{
			code: '38644',
			description: '10 150 BLEED RING 316SS 2 X 1/2 FNPT TAPS',
			matchPercent: 91,
			inventory: 6,
			isMarketBasket: true
		},
		{
			code: '38642',
			description: '10 150 BLEED RING 316SS 1 X 1/2 FNPT TAP',
			matchPercent: 78,
			inventory: 2,
			isMarketBasket: false
		},
		{
			code: '38646',
			description: '10 300 BLEED RING 316SS 2 X 1/2 FNPT TAPS',
			matchPercent: 64,
			inventory: 0,
			isMarketBasket: false
		}
	],
	cgi10: [
		{
			code: '35915',
			description:
				'10 150 CGI FLEX GASKET 316SS INNER RING AND WINDING W/GRAPHITE FILLER',
			matchPercent: 33,
			inventory: 0,
			isMarketBasket: false
		},
		{
			code: '35910',
			description: '10 150 CGI GASKET 316SS OUTER RING GRAPHITE',
			matchPercent: 27,
			inventory: 0,
			isMarketBasket: false
		},
		{
			code: '36716',
			description: '10 150 SCH/10S RFWN FLANGE 316SS IMPORT',
			matchPercent: 19,
			inventory: 45,
			isMarketBasket: false
		}
	],
	gk10: [
		{
			code: '35915',
			description:
				'10 150 CGI FLEX GASKET 316SS INNER RING AND WINDING W/GRAPHITE FILLER',
			matchPercent: 67,
			inventory: 48,
			isMarketBasket: false
		},
		{
			code: '35915D',
			description:
				'10 150 CGI FLEX GASKET 316SS DOMESTIC W/GRAPHITE FILLER',
			matchPercent: 59,
			inventory: 22,
			isMarketBasket: false
		},
		{
			code: '35910',
			description: '10 150 CGI GASKET 316SS OUTER RING GRAPHITE',
			matchPercent: 52,
			inventory: 80,
			isMarketBasket: false
		},
		{
			code: '35917',
			description:
				'10 300 CGI FLEX GASKET 316SS INNER RING W/GRAPHITE FILLER',
			matchPercent: 44,
			inventory: 9,
			isMarketBasket: false
		}
	],
	blind6: [
		{
			code: '36194',
			description: '6 150 RF BLIND FLANGE 316SS IMPORT',
			matchPercent: 96,
			inventory: 0,
			isMarketBasket: true
		},
		{
			code: '36194D',
			description: '6 150 RF BLIND FLANGE 316SS DOMESTIC',
			matchPercent: 88,
			inventory: 34,
			isMarketBasket: true
		},
		{
			code: '36196',
			description: '6 300 RF BLIND FLANGE 316SS IMPORT',
			matchPercent: 71,
			inventory: 58,
			isMarketBasket: false
		}
	]
};

interface CodebookEntry {
	raw: string;
	status: PartCodingStatus;
	code: string;
	description: string;
	inventory: number | null;
	isMarketBasket: boolean | null;
	source: string;
	confidence: number;
	family: string;
}

// What the matcher "knows". Keyed on the raw pasted description — a stand-in
// for the real item-master lookup until this view is wired to the API.
const CODEBOOK: readonly CodebookEntry[] = [
	{
		raw: '8" to 4" stainless reducer',
		status: 'Auto',
		code: '37412',
		description: '8 X 4 SCH/40S CONC REDUCER 316SS BW IMPORT',
		inventory: 26,
		isMarketBasket: true,
		source: 'AI',
		confidence: 92,
		family: 'red8x4'
	},
	{
		raw: `20' 8" sch 40 stainless pipe`,
		status: 'Auto',
		code: '30820',
		description: '8 SCH/40S SMLS PIPE 316SS A312 20FT RL',
		inventory: 1240,
		isMarketBasket: true,
		source: 'AI',
		confidence: 96,
		family: 'pipe8'
	},
	{
		raw: '8" 150# SS Weld Neck Flange',
		status: 'Auto',
		code: '36752',
		description: '8 150 SCH/40S STD RFWN FLANGE 316SS IMPORT',
		inventory: 318,
		isMarketBasket: true,
		source: 'AI',
		confidence: 94,
		family: 'fl8'
	},
	{
		raw: '8" schedule 40 stainless weld neck 45 degree elbow',
		status: 'Auto',
		code: '37145',
		description: '8 SCH/40S 45 DEG LR ELBOW 316SS BW IMPORT',
		inventory: 62,
		isMarketBasket: true,
		source: 'AI',
		confidence: 91,
		family: 'el45'
	},
	{
		raw: '8" schedule 40 stianless weld neck tee',
		status: 'Auto',
		code: '37208',
		description: '8 SCH/40S STRAIGHT TEE 316SS BW IMPORT',
		inventory: 41,
		isMarketBasket: true,
		source: 'AI',
		confidence: 91,
		family: 'tee8'
	},
	{
		raw: '10" - Lug, DI Body, SS Stem, SS Disc, Resilient Seat, Gear',
		status: 'Auto',
		code: '41330',
		description: '10 LUG BFLY DI BODY SS STEM SS DISC EPDM SEAT GEAR OP',
		inventory: 12,
		isMarketBasket: true,
		source: 'AI',
		confidence: 93,
		family: 'bfly10'
	},
	{
		raw: '8" - Lug, SS Body, SS Stem, SS Disc, High Performance, Gear',
		status: 'RFQ',
		code: 'RFQ',
		description: '',
		inventory: null,
		isMarketBasket: null,
		source: 'AI',
		confidence: 5,
		family: 'bfly8hp'
	},
	{
		raw: '8" - SS Body and Disc, Viton Seat, Split Disc Swing',
		status: 'Auto',
		code: '43120',
		description: '8 150 SPLIT DISC WAFER CHECK SS BODY VITON SEAT',
		inventory: 18,
		isMarketBasket: true,
		source: 'AI',
		confidence: 93,
		family: 'chk8'
	},
	{
		raw: 'Ball, 200 psi, FNPT, FP, SS Body, LO Spring Return Handle, NACE',
		status: 'Auto',
		code: '44870',
		description: '2 3000 FNPT FP BALL VALVE 316SS LO SPRING RETURN NACE',
		inventory: 88,
		isMarketBasket: true,
		source: 'AI',
		confidence: 91,
		family: 'ball2'
	},
	{
		raw: 'Bleed Ring w/ 2 x 1/2 in NPT taps',
		status: 'Auto',
		code: '38644',
		description: '10 150 BLEED RING 316SS 2 X 1/2 FNPT TAPS',
		inventory: 6,
		isMarketBasket: true,
		source: 'AI',
		confidence: 91,
		family: 'bleed10'
	},
	{
		raw: '10" 150# SS Weld Neck Flange',
		status: 'Auto',
		code: '36754',
		description: '10 150 SCH/40S STD RFWN FLANGE 316SS IMPORT',
		inventory: 700,
		isMarketBasket: true,
		source: 'AI',
		confidence: 92,
		family: 'fl10'
	},
	{
		raw: '10" 150# SS Wide Flange',
		status: 'Review',
		code: '36754B',
		description: '10 150 SCH/40S STD WIDE FLANGE 316SS IMPORT',
		inventory: 500,
		isMarketBasket: true,
		source: 'AI',
		confidence: 85,
		family: 'fl10w'
	},
	{
		raw: '10" CGI',
		status: 'RFQ',
		code: 'RFQ',
		description: '',
		inventory: null,
		isMarketBasket: null,
		source: 'AI',
		confidence: 5,
		family: 'cgi10'
	},
	{
		raw: '10" 150 CGI Gasket',
		status: 'Review',
		code: '35915',
		description:
			'10 150 CGI FLEX GASKET 316SS INNER RING AND WINDING W/GRAPHITE FILLER',
		inventory: 48,
		isMarketBasket: false,
		source: 'AI',
		confidence: 67,
		family: 'gk10'
	},
	// Confident AI match, but the top hit has 0 on hand — flagged Out of Stock
	// instead of Auto so the ISR catches it before it ships short.
	{
		raw: '6" 150# SS Blind Flange',
		status: 'Out of Stock',
		code: '36194',
		description: '6 150 RF BLIND FLANGE 316SS IMPORT',
		inventory: 0,
		isMarketBasket: true,
		source: 'AI',
		confidence: 96,
		family: 'blind6'
	}
];

const normalize = (value: string): string =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

const CODEBOOK_BY_DESCRIPTION = new Map<string, CodebookEntry>(
	CODEBOOK.map((entry) => [normalize(entry.raw), entry])
);

const FALLBACK_FAMILY = 'cgi10';

interface ParsedLine {
	quantity: number;
	description: string;
}

export function parseLine(line: string): ParsedLine {
	const match = /^(\d+)\s+(.+)$/.exec(line);
	if (!match) {
		return { quantity: 1, description: line.trim() };
	}
	const [, quantityText, description] = match;
	return {
		quantity: Number(quantityText ?? 1),
		description: (description ?? '').trim()
	};
}

// Token-overlap fallback so hand-typed lines still resolve to something.
function fallbackMatch(description: string): CodebookEntry {
	const wanted = new Set(
		normalize(description)
			.split(' ')
			.filter((word) => word.length > 2)
	);
	let best: CodebookEntry | null = null;
	let bestScore = 0;

	for (const entry of CODEBOOK) {
		const have = new Set(
			normalize(entry.raw)
				.split(' ')
				.filter((word) => word.length > 2)
		);
		let score = 0;
		wanted.forEach((word) => {
			if (have.has(word)) score++;
		});
		if (score > bestScore) {
			bestScore = score;
			best = entry;
		}
	}

	if (best && bestScore >= 2 && best.status !== 'RFQ') {
		return {
			...best,
			status: 'Review',
			source: 'AI',
			confidence: Math.max(55, best.confidence - 14)
		};
	}

	return {
		raw: description,
		status: 'RFQ',
		code: 'RFQ',
		description: '',
		inventory: null,
		isMarketBasket: null,
		source: 'AI',
		confidence: 5,
		family: FALLBACK_FAMILY
	};
}

export function buildPartCodingRow(line: string, mark: number): PartCodingRow {
	const { quantity, description } = parseLine(line);
	const hit =
		CODEBOOK_BY_DESCRIPTION.get(normalize(description)) ??
		fallbackMatch(description);

	// 0 on hand is always flagged Out of Stock, independent of match
	// confidence — the problem is inventory, not match quality, so it
	// overrides Auto/Review alike.
	const status: PartCodingStatus =
		hit.inventory === 0 ? 'Out of Stock' : hit.status;

	return {
		id: 'pc' + mark,
		mark,
		quantity,
		customerDescription: description,
		status,
		partCode: hit.code,
		rkDescription: hit.description,
		inventory: hit.inventory,
		isMarketBasket: hit.isMarketBasket,
		source: hit.source,
		confidence: hit.confidence,
		reason: '',
		candidateFamily: hit.family,
		candidates: [],
		repCheck: 'pending'
	};
}

export function candidatesForFamily(
	family: string
): readonly PartCodingCandidate[] {
	return PART_CODING_CANDIDATES[family] ?? [];
}

export function pasteLineCount(pasteText: string): number {
	return pasteText.split(/\r?\n/).filter((line) => line.trim()).length;
}

export function rowMatchesQuery(row: PartCodingRow, query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return [
		row.mark,
		row.customerDescription,
		row.partCode,
		row.rkDescription,
		row.source,
		row.reason
	]
		.join(' ')
		.toLowerCase()
		.includes(q);
}

function csvField(value: string | number | null): string {
	const str = String(value ?? '');
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

export function buildPartCodingCsv(
	rows: readonly PartCodingRow[],
	isr: string,
	exportedAt: Date
): string {
	const header = [
		'Mark',
		'Status',
		'Quantity',
		'Customer Description',
		'Part Code',
		'RK Description',
		'RK Inv',
		'MBA',
		'Source',
		'Conf',
		'Reason',
		'ISR',
		'Submitted'
	];
	const submittedLabel = exportedAt.toLocaleString(undefined, {
		month: 'numeric',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
	const body = rows.map((row) =>
		[
			row.mark,
			row.status,
			row.quantity,
			row.customerDescription,
			row.status === 'RFQ' ? 'RFQ' : row.partCode,
			row.rkDescription,
			row.inventory ?? '',
			row.isMarketBasket === null
				? ''
				: row.isMarketBasket
					? 'Yes'
					: 'No',
			row.source,
			row.confidence + '%',
			row.reason,
			isr,
			submittedLabel
		]
			.map(csvField)
			.join(',')
	);

	return [header.map(csvField).join(','), ...body].join('\r\n');
}
